import React from 'react';
import { Maintenance, MaintenanceSeverity } from '@/types/common';
import {
  getMaintenanceReasonLabel,
  getMaintenanceCategoryLabel,
  getCostResponsibilityLabel,
  getMaintenanceSeverityLabel,
} from '../../../utils/maintenanceEnums';

interface MaintenanceDetailViewProps {
  detailTab: 'details' | 'notes';
  initialData?: Maintenance | null;
  onTabChange: (tab: 'details' | 'notes') => void;
}

const MaintenanceDetailView: React.FC<MaintenanceDetailViewProps> = ({
  detailTab,
  initialData,
  onTabChange,
}) => {
  return (
    <div className="flex-1 flex flex-col h-full">
      <div className="px-6 border-b border-gray-200 flex-shrink-0 sticky top-0 bg-white z-10">
        <nav className="flex space-x-8">
          <button
            onClick={() => onTabChange('details')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${detailTab === 'details'
                ? 'border-[#5E2AAC]'
                : 'border-transparent text-gray-500 hover:text-gray-400 hover:border-gray-300'
              }`}
          >
            General Data
          </button>
          <button
            onClick={() => onTabChange('notes')}
            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${detailTab === 'notes'
                ? 'border-[#5E2AAC]'
                : 'border-transparent text-gray-500 hover:text-gray-400 hover:border-gray-300'
              }`}
          >
            Details
          </button>
        </nav>
      </div>

      <div className="flex-1">
        {detailTab === 'details' ? (
          <div className="space-y-6 pt-6">
            <div className="grid grid-cols-2 px-6 gap-4">
              <div className="col-span-2 border-b border-gray-200 flex">
                <div className="flex-1 pr-4">
                  <label className="block text-sm font-medium text-gray-400">
                    Plate Number
                  </label>
                  <p className="mt-1 text-sm font-regular  mb-2 rounded-md">
                    {(initialData as any)?.vehiclePlatNomor || '-'}
                  </p>
                </div>
                <div className="flex-1 pl-4"></div>
              </div>

              <div className="col-span-2 border-b border-gray-200 flex">
                <div className="flex-1 pr-4">
                  <label className="block text-sm font-medium text-gray-400">
                    OEM Name
                  </label>
                  <p className="mt-1 text-sm font-regular mb-2 rounded-md">
                    {(initialData as any)?.vehicleOemName || '-'}
                  </p>
                </div>
                <div className="flex-1 pl-4">
                  <label className="block text-sm font-medium text-gray-400">
                    Model Name
                  </label>
                  <p className="mt-1 text-sm font-regular mb-2 rounded-md">
                    {(initialData as any)?.vehicleModelName || '-'}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-700 rounded-t-lg heading-field">
              Maintenance Detail
            </div>
            <div className="mt-6 px-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 border-b border-gray-200 flex">
                  <div className="flex-1 pr-4">
                    <label className="block text-sm font-medium text-gray-400">
                      Reason
                    </label>
                    <p className="mt-1 text-sm font-regular  mb-2 rounded-md">
                      {(initialData as any)?.reason
                        ? getMaintenanceReasonLabel(
                          (initialData as any).reason as any
                        )
                        : '-'}
                    </p>
                  </div>
                  <div className="flex-1 pl-4">
                    <label className="block text-sm font-medium text-gray-400">
                      Maintenance Details
                    </label>
                    <p className="mt-1 text-sm font-regular  mb-2 rounded-md">
                      {(initialData as any)?.categories &&
                        (initialData as any)?.categories.length > 0
                        ? (initialData as any).categories
                          .map((cat: string) =>
                            getMaintenanceCategoryLabel(cat as any)
                          )
                          .join(', ')
                        : '-'}
                    </p>
                  </div>
                </div>

                <div className="col-span-2 border-b border-gray-200 flex-1">
                  <label className="block text-sm font-medium text-gray-400">
                    Description
                  </label>
                  <p className="mt-1 text-sm font-regular  mb-2 rounded-md">
                    {(initialData as any)?.issueDescription || '-'}
                  </p>
                </div>

                <div className="col-span-2 border-b border-gray-200 flex">
                  <div className="flex-1 pr-4">
                    <label className="block text-sm font-medium text-gray-400">
                      Tanggal Maintenance
                    </label>
                    <p className="mt-1 text-sm font-regular  mb-2 rounded-md">
                      {(initialData as any)?.maintenanceDate
                        ? new Date(
                          (initialData as any).maintenanceDate
                        ).toLocaleDateString('en-GB')
                        : '-'}
                    </p>
                  </div>
                  <div className="flex-1 pl-4">
                    <label className="block text-sm font-medium text-gray-400">
                      Expected Completion Date
                    </label>
                    <p className="mt-1 text-sm font-regular mb-2 rounded-md">
                      {(initialData as any)?.expectedCompletionDate
                        ? new Date(
                          (initialData as any).expectedCompletionDate
                        ).toLocaleDateString('en-GB')
                        : '-'}
                    </p>
                  </div>
                </div>

                <div className="col-span-2 border-b border-gray-200 flex-1">
                  <label className="block text-sm font-medium text-gray-400">
                    Severity
                  </label>
                  <div className="mt-1 mb-2">
                    {(initialData as any)?.severity ? (
                      <span
                        className={`inline-flex items-center text-sm font-medium`}
                      >
                        {getMaintenanceSeverityLabel(
                          (initialData as any).severity as MaintenanceSeverity
                        )}
                      </span>
                    ) : (
                      <p className="text-sm font-regular">-</p>
                    )}
                  </div>
                </div>

                <div className="col-span-2 border-b border-gray-200 flex">
                  <div className="flex-1 pr-4">
                    <label className="block text-sm font-medium text-gray-400">
                      Estimated Cost
                    </label>
                    <p className="mt-1 text-sm font-regular  mb-2 rounded-md">
                      {(initialData as any)?.estimatedCost
                        ? new Intl.NumberFormat('id-ID', {
                          style: 'currency',
                          currency: 'IDR',
                          minimumFractionDigits: 0,
                        }).format((initialData as any).estimatedCost)
                        : '-'}
                    </p>
                  </div>
                  <div className="flex-1 pl-4">
                    <label className="block text-sm font-medium text-gray-400">
                      Biaya Ditanggung
                    </label>
                    <p className="mt-1 text-sm font-regular  mb-2 rounded-md">
                      {(initialData as any)?.costResponsibility
                        ? getCostResponsibilityLabel(
                          (initialData as any).costResponsibility
                        )
                        : '-'}
                    </p>
                  </div>
                </div>

                <div className="col-span-2 border-b border-gray-200 flex-1">
                  <label className="block text-sm font-medium text-gray-400">
                    OEM Service Center
                  </label>
                  <p className="mt-1 text-sm font-regular  mb-2 rounded-md">
                    {(initialData as any)?.oemServiceCenter || '-'}
                  </p>
                </div>

                <div className="col-span-2 border-b border-gray-200 flex-1">
                  <label className="block text-sm font-medium text-gray-400">
                    Notes
                  </label>
                  <p className="mt-1 text-sm font-regular  mb-2 rounded-md">
                    {(initialData as any)?.notes || '-'}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-700 rounded-t-lg heading-field">
                Info
              </div>
              <div className="grid grid-cols-2 gap-4 mt-2 px-6">
                <div className="col-span-2 border-b border-gray-200 flex-1">
                  <div className="flex flex-row grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Created By
                      </label>
                      <p className="mt-1 text-sm font-regular   mb-2 rounded-md max-w-[150px] break-words">
                        {(initialData as any)?.createdBy || '-'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Created Date
                      </label>
                      <p className="mt-1 text-sm font-regular   mb-2 rounded-md max-w-[150px] break-words">
                        {(initialData as any)?.createdAt
                          ? new Date(
                            (initialData as any).createdAt
                          ).toLocaleDateString('en-GB') +
                          ' at ' +
                          new Date(
                            (initialData as any).createdAt
                          ).toLocaleTimeString('en-GB', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                          : '-'}
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Last Updated By
                  </label>
                  <p className="mt-1 text-sm font-regular mb-2 rounded-md max-w-[150px] break-words">
                    {(initialData as any)?.updatedBy || '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Last Updated
                  </label>
                  <p className="mt-1 text-sm font-regular mb-2 rounded-md max-w-[150px] break-words">
                    {(initialData as any)?.updatedAt
                      ? new Date(
                        (initialData as any).updatedAt
                      ).toLocaleDateString('en-GB') +
                      ' at ' +
                      new Date(
                        (initialData as any).updatedAt
                      ).toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                      : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="px-6">
              <h4 className="text-sm font-medium text-gray-400 mb-4">
                Foto Kendaraan
              </h4>
              <div className="border-2 border-gray-200 rounded-lg w-full">
                {(initialData as any)?.vehicleImageURL ? (
                  <div className="p-4">
                    <img
                      src={(initialData as any).vehicleImageURL}
                      alt="Maintenance vehicle"
                      className="w-full h-64 object-cover rounded-lg"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                    <div className="hidden p-8 flex flex-col justify-center items-center text-center">
                      <div className="text-gray-300 text-4xl mb-4">
                        <svg
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          className="w-12 h-12 mx-auto"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <p className="text-gray-500 font-medium">
                        Failed to load image
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        The image could not be displayed
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 flex flex-col justify-center items-center text-center">
                    <div className="text-gray-300 text-4xl mb-4">
                      <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        className="w-12 h-12 mx-auto"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <p className="text-gray-500 font-medium">
                      No maintenance image uploaded
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Image was not provided during maintenance creation
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6">
              <h4 className="text-sm font-medium text-gray-400 mb-4">Notes</h4>
              <p className="text-sm font-regular px-1 mb-2 rounded-md">
                {(initialData as any)?.notes || '-'}
              </p>
            </div>

            <div>
              <div className="bg-gray-50 px-6 py-2 text-sm font-medium text-gray-700 rounded-t-lg heading-field">
                Info
              </div>
              <div className="grid grid-cols-2 gap-4 px-6">
                <div className="col-span-2 border-b border-gray-200 flex-1">
                  <div className="flex flex-row grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Created By
                      </label>
                      <p className="mt-1 text-sm font-regular mb-2 rounded-md max-w-[150px] break-words">
                        {(initialData as any)?.createdBy || '-'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        Created Date
                      </label>
                      <p className="mt-1 text-sm font-regular mb-2 rounded-md max-w-[150px] break-words">
                        {(initialData as any)?.createdAt
                          ? new Date(
                            (initialData as any).createdAt
                          ).toLocaleDateString('en-GB') +
                          ' at ' +
                          new Date(
                            (initialData as any).createdAt
                          ).toLocaleTimeString('en-GB', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                          : '-'}
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Last Updated By
                  </label>
                  <p className="mt-1 text-sm font-regular mb-2 rounded-md max-w-[150px] break-words">
                    {(initialData as any)?.updatedBy || '-'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400">
                    Last Updated
                  </label>
                  <p className="mt-1 text-sm font-regular mb-2 rounded-md max-w-[150px] break-words">
                    {(initialData as any)?.updatedAt
                      ? new Date(
                        (initialData as any).updatedAt
                      ).toLocaleDateString('en-GB') +
                      ' at ' +
                      new Date(
                        (initialData as any).updatedAt
                      ).toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                      : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaintenanceDetailView;
